import { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import sliderLinks from '../../json/sliderLinks';
import './slide.css';

const CarouselSlider = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  const [direction, setDirection] = useState(0); // -1: izquierda, 1: derecha
  const sliderRef = useRef(null);
  const autoPlayRef = useRef(null);
  const autoPlayInterval = 5000; // Intervalo de autoplay en milisegundos (5 segundos)

  // Calcular índices para las tarjetas
  const prevIndex = (currentIndex - 1 + sliderLinks.length) % sliderLinks.length;
  const nextIndex = (currentIndex + 1) % sliderLinks.length;
  const farNextIndex = (currentIndex + 2) % sliderLinks.length;
  
  // Detectar cambios de tamaño de pantalla
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Configurar autoplay
  useEffect(() => {
    const startAutoPlay = () => {
      stopAutoPlay();
      autoPlayRef.current = setInterval(() => {
        setDirection(1);
        setCurrentIndex((prevIndex) => (prevIndex + 1) % sliderLinks.length);
      }, autoPlayInterval);
    };
    
    const stopAutoPlay = () => {
      if (autoPlayRef.current) {
        clearInterval(autoPlayRef.current);
      }
    };
    
    // Iniciar autoplay
    startAutoPlay();
    
    // Pausar autoplay cuando el mouse está sobre el slider
    const slider = sliderRef.current;
    if (slider) {
      slider.addEventListener('mouseenter', stopAutoPlay);
      slider.addEventListener('mouseleave', startAutoPlay);
      slider.addEventListener('touchstart', stopAutoPlay, { passive: true });
      slider.addEventListener('touchend', startAutoPlay, { passive: true });
    }
    
    return () => {
      stopAutoPlay();
      if (slider) {
        slider.removeEventListener('mouseenter', stopAutoPlay);
        slider.removeEventListener('mouseleave', startAutoPlay);
        slider.removeEventListener('touchstart', stopAutoPlay);
        slider.removeEventListener('touchend', startAutoPlay);
      }
    };
  }, [sliderLinks.length]);

  // Manejar el clic en el botón siguiente
  const handleNext = () => {
    setDirection(1);
    setCurrentIndex((prevIndex) => (prevIndex + 1) % sliderLinks.length);
  };

  // Manejar el clic en el botón anterior
  const handlePrev = () => {
    setDirection(-1);
    setCurrentIndex((prevIndex) => (prevIndex - 1 + sliderLinks.length) % sliderLinks.length);
  };

  // Gestionar el inicio del arrastre
  const handleDragStart = (e) => {
    e.preventDefault();
    setIsDragging(true);
    const clientX = e.clientX || (e.touches && e.touches[0].clientX) || 0;
    setDragStart(clientX);
  };

  // Gestionar el movimiento durante el arrastre
  const handleDragMove = (e) => {
    if (!isDragging) return;
    
    const clientX = e.clientX || (e.touches && e.touches[0].clientX) || 0;
    if (!clientX) return;
    
    const offset = clientX - dragStart;
    setDragOffset(offset);
    
    e.preventDefault();
  };

  // Gestionar el final del arrastre
  const handleDragEnd = () => {
    if (isDragging) {
      if (dragOffset > 50) {
        setDirection(-1);
        handlePrev();
      } else if (dragOffset < -50) {
        setDirection(1);
        handleNext();
      }
      setIsDragging(false);
      setDragOffset(0);
    }
  };

  // Variantes de animación para las tarjetas
  const cardVariants = {
    enter: (direction) => ({
      x: direction < 0 ? '100%' : '-100%',
      opacity: 0,
      scale: 0.95,
    }),
    center: {
      x: 0,
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      },
    },
    exit: (direction) => ({
      x: direction < 0 ? '-100%' : '100%',
      opacity: 0,
      scale: 0.95,
      transition: {
        duration: 0.4,
        ease: "easeIn"
      },
    }),
  };

  // Calcular desplazamientos para tarjetas adyacentes
  const getTranslateX = (position) => {
    let baseOffset;
    let farOffset;
    
    // Cálculo de offset basado en el ancho de pantalla
    if (windowWidth < 576) { // Móvil
      baseOffset = windowWidth * 0.7;
      farOffset = windowWidth * 0.9;
    } else if (windowWidth < 992) { // Tablet
      baseOffset = windowWidth * 0.55;
      farOffset = windowWidth * 0.8;
    } else { // Desktop
      baseOffset = windowWidth * 0.26;
      farOffset = windowWidth * 0.7;
    }
    
    switch (position) {
      case 'prev':
        return `-${baseOffset}px`;
      case 'next':
        return `${baseOffset}px`;
      case 'farNext':
        return `${farOffset}px`;
      default:
        return '0';
    }
  };

  // Cálculo del ancho de las tarjetas
  const getCardWidth = () => {
    if (windowWidth < 576) return windowWidth * 0.85; // Móvil
    if (windowWidth < 992) return 400; // Tablet
    return 420; // Desktop
  };

  return (
    <div className="position-relative mx-auto py-4 carousel-enlaces">
      <h3 
        className="mb-3 fw-bold" 
        style={{ 
          fontFamily: "Montserrat-SemiBold",
          color: "black",
          fontWeight: "900", 
          fontSize: "1.75rem" 
        }}
      >SITIOS DE INTERÉS
      </h3>
      <hr className='hr-gob'/>
      {/* Contenedor principal del carrusel */}
      <div 
        ref={sliderRef} 
        className="position-relative overflow-hidden py-4 user-select-none swiper"
        style={{ 
          minHeight: '250px',
          cursor: isDragging ? 'grabbing' : 'grab',
          touchAction: 'none'
        }}
        onMouseDown={handleDragStart}
        onMouseMove={handleDragMove}
        onMouseUp={handleDragEnd}
        onMouseLeave={handleDragEnd}
        onTouchStart={handleDragStart}
        onTouchMove={handleDragMove}
        onTouchEnd={handleDragEnd}
      >
        {/* Gradiente izquierdo */}
        <div className="position-absolute start-0 top-0 h-100" 
          style={{ 
            width: windowWidth >= 576 ? '8rem' : '3rem',
            background: 'linear-gradient(to right, rgba(245,245,247,0.95) 0%, rgba(255,255,255,0) 100%)',
            zIndex: 100,
            pointerEvents: 'none'
          }}
        />

        {/* Tarjetas del carrusel */}
        <div className="d-flex justify-content-center align-items-center h-100 position-relative swiper-wrapper">
          <AnimatePresence initial={false} custom={direction} mode="wait">
            {/* Tarjeta previa - solo visible en pantallas más grandes */}
            {windowWidth >= 576 && (
              <motion.div 
                key={`prev-${currentIndex}`}
                className="position-absolute swiper-slide"
                custom={direction}
                initial={{ opacity: 0 }}
                animate={{
                  x: getTranslateX('prev'),
                  opacity: 0.7,
                  scale: 0.9,
                  transition: { duration: 0.4 }
                }}
                exit={{ opacity: 0, transition: { duration: 0.2 } }}
                style={{ 
                  width: `${getCardWidth()}px`,
                  transform: isDragging ? `translateX(calc(${getTranslateX('prev')} + ${dragOffset}px))` : undefined,
                  transition: isDragging ? 'none' : undefined,
                  zIndex: 10
                }}
              >
                <CardContent 
                  data={sliderLinks[prevIndex]} 
                  featured={false}
                />
              </motion.div>
            )}
            
            {/* Tarjeta actual */}
            <motion.div
              key={`current-${currentIndex}`}
              variants={cardVariants}
              custom={direction}
              initial="enter"
              animate="center"
              exit="exit"
              className="swiper-slide swiper-slide-active"
              style={{ 
                width: `${getCardWidth()}px`,
                zIndex: 20
              }}
            >
              <CardContent 
                data={sliderLinks[currentIndex]} 
                featured={true}
              />
            </motion.div>
            
            {/* Próxima tarjeta - solo visible en pantallas más grandes */}
            {windowWidth >= 576 && (
              <motion.div 
                key={`next-${currentIndex}`}
                className="position-absolute swiper-slide"
                custom={direction}
                initial={{ opacity: 0 }}
                animate={{
                  x: getTranslateX('next'),
                  opacity: 0.7,
                  scale: 0.9,
                  transition: { duration: 0.4 }
                }}
                exit={{ opacity: 0, transition: { duration: 0.5 } }}
                style={{ 
                  width: `${getCardWidth()}px`,
                  transform: isDragging ? `translateX(calc(${getTranslateX('next')} + ${dragOffset}px))` : undefined,
                  transition: isDragging ? 'none' : undefined,
                  zIndex: 10
                }}
              >
                <CardContent 
                  data={sliderLinks[nextIndex]} 
                  featured={false}
                />
              </motion.div>
            )}
            
            {/* Tarjeta extrema derecha (far next) - solo visible en pantallas grandes */}
            {windowWidth >= 768 && (
              <motion.div 
                key={`far-next-${currentIndex}`}
                className="position-absolute swiper-slide"
                custom={direction}
                initial={{ opacity: 0 }}
                animate={{
                  x: getTranslateX('farNext'),
                  opacity: 0.4,
                  scale: 0.8,
                  transition: { duration: 0.7 }
                }}
                exit={{ opacity: 0, transition: { duration: 0.5 } }}
                style={{ 
                  width: `${getCardWidth()}px`,
                  transform: isDragging ? `translateX(calc(${getTranslateX('farNext')} + ${dragOffset}px))` : undefined,
                  transition: isDragging ? 'none' : undefined,
                  zIndex: 5,
                  filter: 'blur(1px)'
                }}
              >
                <CardContent 
                  data={sliderLinks[farNextIndex]} 
                  featured={false}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Gradiente derecho */}
        <div className="position-absolute end-0 top-0 h-100" 
          style={{ 
            width: windowWidth >= 576 ? '8rem' : '3rem',
            background: 'linear-gradient(to left, rgba(245,245,247,0.95) 0%, rgba(255,255,255,0) 100%)',
            zIndex: 10,
            pointerEvents: 'none'
          }}
        />
      </div>

      {/* Controles de navegación */}
      <div className="d-flex justify-content-center mt-3 gap-3">
        <button 
          onClick={handlePrev}
          className="bg-light rounded-circle p-2 border-0 shadow-sm"
          style={{ transition: 'all 0.5s' }}
          aria-label="Anterior"
        >
          <ChevronLeft size={24} />
        </button>
        <button 
          onClick={handleNext}
          className="bg-light rounded-circle p-2 border-0 shadow-sm"
          style={{ transition: 'all 0.5s' }}
          aria-label="Siguiente"
        >
          <ChevronRight size={24} />
        </button>
      </div>
      
      {/* Indicadores de posición */}
      <div className="d-flex justify-content-center mt-3 flex-wrap">
        {sliderLinks.map((_, index) => (
          <button
            key={index}
            onClick={() => {
              setDirection(index > currentIndex ? 1 : -1);
              setCurrentIndex(index);
            }}
            className="border-0 mx-1 rounded-pill"
            style={{
              height: '8px',
              width: currentIndex === index ? '16px' : '8px',
              backgroundColor: currentIndex === index ? '#9E2343' : '#dee2e6',
              transition: 'all 0.5s',
              padding: 0
            }}
          />
        ))}
      </div>
    </div>
  );
};

// Componente para cada tarjeta de contenido
const CardContent = ({ data, featured = false }) => {
  // Determinar el tipo de enlace (interno o externo)
  const link = data.linkInt || data.linkExt || '#';
  const isExternal = Boolean(data.linkExt);
  
  return (
    <div className="slide content-slide">
      <div className="w-100 bg-white rounded shadow h-100 p-4">
        {/* Logo con enlace */}
        <div className="logo-enlace text-center mb-3">
          <a 
            href={link} 
            target={isExternal ? "_blank" : "_self"} 
            rel={isExternal ? "noopener noreferrer" : ""}
          >
            <img 
              src={data.image} 
              alt={data.title} 
              className="img-fluid" 
              style={{ maxHeight: '380px' }}
            />
          </a>
        </div>
        
        {/* Título con enlace */}
        <a 
          href={link} 
          target={isExternal ? "_blank" : "_self"} 
          rel={isExternal ? "noopener noreferrer" : ""}
          className="title-enlace d-block text-center fw-bold mb-3 text-decoration-none"
          style={{ 
            color: '#9E2343',
            fontSize: '1.25rem'
          }}
        >
          <p>{data.title}</p>
        </a>
        
        {/* Descripción */}
        <p 
          className="description text-center m-0" 
          style={{
            fontSize: '0.9rem',
            color: '#555'
          }}
        >
          {data.description}
        </p>
      </div>
    </div>
  );
};

export default CarouselSlider;